import {inject, injectable} from "inversify";
import type {IApiResponse, IHttpClientService} from "data/services/http";
import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";

export interface IPrediction {
	contestId: number;
	playerId: number;
}

export interface ISavePredictionParams extends IPrediction {
	token?: string;
	guestUid?: string;
}

export interface ITransferGuestPredictionsParams {
	guestUid: string;
}

export interface IGetPredictionsParams {
	guestUid?: string;
}

export interface IGetContestPredictionParams extends IGetPredictionsParams {
	contestId: number;
}

export type TPredictionResponse = IApiResponse<{prediction: IPrediction}>;
export type TPredictionsResponse = IApiResponse<{predictions: IPrediction[]}>;

export interface IGetShareImageParams {
	guestUid?: string;
	contestId: number;
}

export interface IShareImage {
	shareImage: {fileName: string};
}
export interface IPredictionApiProvider {
	save: (params: ISavePredictionParams) => Promise<AxiosResponse<TPredictionResponse>>;
	get: (params: IGetContestPredictionParams) => Promise<AxiosResponse<TPredictionResponse>>;
	getShareImage: (
		params: IGetShareImageParams
	) => Promise<AxiosResponse<IApiResponse<IShareImage>>>;

	getWinnerShareImage: (
		params: IGetShareImageParams
	) => Promise<AxiosResponse<IApiResponse<IShareImage>>>;

	predictions: (params?: IGetPredictionsParams) => Promise<AxiosResponse<TPredictionsResponse>>;
	transfer: (
		params: ITransferGuestPredictionsParams
	) => Promise<AxiosResponse<TPredictionsResponse>>;
}

@injectable()
export class PredictionApiProvider implements IPredictionApiProvider {
	constructor(@inject(Bindings.ApiHTTPClient) private _http: IHttpClientService) {}

	save = ({token, ...rest}: ISavePredictionParams) =>
		this._http.post<TPredictionResponse>("pog/prediction", {
			...rest,
			"g-recaptcha-response": token,
		});

	get = ({contestId, ...params}: IGetContestPredictionParams) =>
		this._http.get<TPredictionResponse>(`pog/prediction/${contestId}`, params);

	getShareImage = ({contestId, ...params}: IGetShareImageParams) =>
		this._http.get<IApiResponse<IShareImage>>(
			`pog/prediction/${contestId}/share-image`,
			params
		);

	getWinnerShareImage = ({contestId, ...params}: IGetShareImageParams) =>
		this._http.get<IApiResponse<IShareImage>>(
			`pog/contest/${contestId}/winner/share-image`,
			params
		);

	predictions = (params?: IGetPredictionsParams) =>
		this._http.get<TPredictionsResponse>(`pog/predictions`, params);

	transfer = (params?: ITransferGuestPredictionsParams) =>
		this._http.post<TPredictionsResponse>(`pog/check-guest-predictions`, params);
}
